import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Bell, BellRing, BellOff, Check, Sparkles } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { getExistingSubscription, subscribeToPush, unsubscribeFromPush } from "@/lib/push";

export default function NotificationBell() {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [count, setCount] = useState(0);
    const [pushSubscribed, setPushSubscribed] = useState(false);
    const [pushBusy, setPushBusy] = useState(false);

    const loadCount = async () => {
        try {
            const { data } = await api.get("/notifications/unread-count");
            setCount(data.count || 0);
        } catch (_) {}
    };
    const loadList = async () => {
        try {
            const { data } = await api.get("/notifications");
            setItems(data);
        } catch (_) {}
    };

    useEffect(() => {
        loadCount();
        const i = setInterval(loadCount, 60_000);
        return () => clearInterval(i);
    }, []);

    useEffect(() => {
        getExistingSubscription()
            .then((sub) => setPushSubscribed(!!sub))
            .catch(() => setPushSubscribed(false));
    }, []);

    useEffect(() => {
        if (open) {
            loadList();
        }
    }, [open]);

    const markAllRead = async () => {
        await api.post("/notifications/read-all");
        await loadList();
        setCount(0);
    };

    const markOne = async (id) => {
        await api.post(`/notifications/${id}/read`);
        setItems((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
        loadCount();
    };

    const togglePush = async () => {
        setPushBusy(true);
        try {
            if (pushSubscribed) {
                await unsubscribeFromPush();
                setPushSubscribed(false);
                toast.success("Notificações push desativadas");
            } else {
                await subscribeToPush();
                setPushSubscribed(true);
                toast.success("Notificações push ativadas!");
            }
        } catch (e) {
            toast.error(e.message || "Não foi possível ativar notificações push");
        } finally {
            setPushBusy(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <button
                    className="relative rounded-full border border-[#E2DCD5] bg-white h-9 w-9 grid place-items-center hover:bg-[#F3EFEA]"
                    data-testid="notifications-bell"
                    title="Notificações"
                >
                    <Bell className="h-4 w-4 text-slate-700" />
                    {count > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D96B43] text-white text-[10px] font-bold grid place-items-center">
                            {count > 9 ? "9+" : count}
                        </span>
                    )}
                </button>
            </SheetTrigger>
            <SheetContent side="right" data-testid="notifications-sheet" className="w-full sm:max-w-md">
                <SheetHeader>
                    <SheetTitle className="flex items-center justify-between">
                        <span>Notificações</span>
                        {items.some((n) => !n.read) && (
                            <Button variant="ghost" size="sm" onClick={markAllRead} data-testid="mark-all-read">
                                <Check className="h-4 w-4 mr-1" /> Marcar todas
                            </Button>
                        )}
                    </SheetTitle>
                </SheetHeader>

                <div className="flex items-center justify-between rounded-xl border border-[#E2DCD5] bg-[#F3EFEA] px-3 py-2.5 mt-3">
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                        {pushSubscribed ? <BellRing className="h-4 w-4 text-[#D96B43]" /> : <BellOff className="h-4 w-4 text-slate-400" />}
                        {pushSubscribed ? "Notificações no celular ativas" : "Ativar notificações no celular"}
                    </div>
                    <Button
                        size="sm"
                        variant={pushSubscribed ? "outline" : "default"}
                        onClick={togglePush}
                        disabled={pushBusy}
                        data-testid="toggle-push-button"
                        className={pushSubscribed ? "rounded-full" : "rounded-full bg-[#D96B43] hover:bg-[#C25933] text-white"}
                    >
                        {pushSubscribed ? "Desativar" : "Ativar"}
                    </Button>
                </div>

                <div className="mt-4 space-y-2 overflow-y-auto max-h-[70vh]">
                    {items.length === 0 ? (
                        <div className="text-center py-14 text-slate-500">
                            <Sparkles className="h-8 w-8 mx-auto text-[#D96B43]" />
                            <p className="mt-3 font-semibold text-slate-700">Nada por aqui ainda</p>
                            <p className="text-sm">O lembrete diário aparece aqui no horário escolhido.</p>
                        </div>
                    ) : (
                        items.map((n) => (
                            <button
                                key={n.id}
                                onClick={() => !n.read && markOne(n.id)}
                                data-testid={`notification-${n.id}`}
                                className={`w-full text-left rounded-xl border p-3 ${n.read ? "border-[#E2DCD5] bg-white" : "border-[#F2CBB6] bg-[#FFF1EA]"}`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="font-semibold text-sm text-slate-900">{n.title}</div>
                                    {!n.read && <span className="h-2 w-2 rounded-full bg-[#D96B43] mt-1.5 shrink-0" />}
                                </div>
                                <div className="text-sm text-slate-600 mt-1">{n.body}</div>
                                <div className="text-[11px] text-slate-400 mt-2">
                                    {safeFromNow(n.created_at)}
                                </div>
                            </button>
                        ))
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}

function safeFromNow(iso) {
    try {
        return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: ptBR });
    } catch (_) {
        return "";
    }
}
